/* FLUSSO · CREAZIONE DI UN TRADEBOT
 *
 * È il punto in cui una configurazione misurata diventa denaro a mercato. Tutto
 * il resto dell'interfaccia guarda numeri; qui si firma.
 *
 * Le quattro cose che questa schermata deve fare bene, in ordine:
 *
 *  1. `strategyParams` arriva dal backtest INTERO. Non si filtra, non si
 *     riordina, non si converte, e nessun valore predefinito viene applicato
 *     sopra. La copia e la sua verifica stanno in `creazione.ts`: se la verifica
 *     trova un'anomalia, il bot non nasce. Se il backtest non porta i
 *     parametri, il bot non nasce e si dice perché.
 *  2. I parametri sono TUTTI a schermo, aperti, senza un pannello da aprire.
 *     È l'unico momento in cui chi mette denaro vero può confrontarli con il
 *     backtest da cui è partito e accorgersi di un errore.
 *  3. Ogni valore cambiato rispetto al backtest è dichiarato — accanto al campo
 *     e in un riepilogo — perché da quel momento i risultati misurati su quel
 *     backtest non valgono più per questo bot.
 *  4. `useRealMoney` non è un interruttore fra gli altri: sta da solo, in fondo,
 *     parte da «simulato» e per accendersi chiede una conferma in cui l'importo
 *     che si sta per impegnare è scritto per intero.
 *
 * Cosa NON fa, e perché: non ricostruisce il selettore di token del frontend
 * precedente. Su catena l'`asset` e la `currency` sono i ticker on-chain e si
 * scrivono, con accanto il confronto col backtest — che riconosce da sé se
 * `BTCB` è il `BTC` misurato. Annotato in RICHIESTE-API.md (T10).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avvertenza,
  Avvisi,
  Campo,
  ContenitoreDati,
  Distintivo,
  DistintivoPeriodo,
  IconaStrategia,
  IntestazionePagina,
  Modale,
  Occhiello,
  Pulsante,
  Scheda,
  Selettore,
  usaAvvisi,
  type VoceSelettore,
} from '../../componenti';
import {
  backtest as apiBacktest,
  eStantio,
  exchanges,
  marketWatchers,
  tokens,
  tradebots,
  usaRichiesta,
} from '../../api';
import type {
  Backtest,
  Catena,
  Dex,
  EsitoProvaRpc,
  MarketWatcher,
  StrategiaDisponibile,
  Tradebot,
} from '../../api';
import {
  drawdown as fmtDrawdown,
  durataPeriodo,
  num,
  ora,
  pct,
  periodo as fmtPeriodo,
  timeframe,
} from '../../lib/formato';
import { tonoRendimento } from '../../lib/metriche';
import { normalizzaAsset, normalizzaValuta } from '../../lib/chiaveConfig';
import {
  CAPITALE_MINIMO,
  caricaDatiBersagli,
  costruisciBersagli,
  leggiCapitale,
  MAX_WALLET,
  motivoBlocco,
  quantiBersagli,
  type Bersaglio,
} from './bersagli';
import { RigaBersaglio } from './FoglioClona';
import { Interruttore } from './Comuni';
import {
  bozzaIniziale,
  bozzaValida,
  copiaParametri,
  corpoCreazione,
  eDiverso,
  elencoParametri,
  ETICHETTE_DEX,
  modificabile,
  motivoRicettaInutilizzabile,
  ricettaDaBacktest,
  scostamentiRicetta,
  SLIPPAGE_PREDEFINITO,
  stimaWarmup,
  testoValore,
  valoreDaBozza,
  type Bozza,
  type ConfigurazioneDex,
  type FormaRicetta,
  type ParametroRicetta,
  type Ricetta,
  type Scostamento,
} from './creazione';
import './tradebot.css';

/** I tre provider che il backend sa interrogare. */
const DEX_DISPONIBILI: Dex[] = ['ZEROX', 'ONEINCH', 'VELORA'];

type Sede = 'account' | 'wallet';

export function NuovoTradebot() {
  const naviga = useNavigate();
  const [parametriUrl] = useSearchParams();
  const { avvisi, mostra } = usaAvvisi();

  /* `daBacktest` è la ragione per cui questa schermata esiste: senza, si parte
     da zero e nessuna misura sostiene i parametri. Le due forme si distinguono
     a schermo, non in silenzio. */
  const grezzoBacktest = parametriUrl.get('daBacktest');
  const idBacktest = grezzoBacktest != null && /^\d+$/.test(grezzoBacktest)
    ? Number(grezzoBacktest)
    : null;
  const backtestRichiesto = grezzoBacktest != null;

  /* ---------- dati ---------- */

  const richiestaBacktest = usaRichiesta(
    (segnale) => apiBacktest.uno(idBacktest as number, segnale),
    [idBacktest],
    { attiva: idBacktest != null, eVuoto: (b) => b == null },
  );

  const richiestaStrategie = usaRichiesta(() => apiBacktest.strategie(), []);
  const richiestaMercati = usaRichiesta(
    () => marketWatchers.elenco({ sort: ['asset,ASC'] }),
    [],
  );

  /* ---------- forma ---------- */

  const [sede, setSede] = useState<Sede>('account');
  const [bersaglioScelto, setBersaglioScelto] = useState<number | null>(null);
  const [capitaleGrezzo, setCapitaleGrezzo] = useState('');
  const [saldoParziale, setSaldoParziale] = useState(true);
  const [denaroVero, setDenaroVero] = useState(false);
  const [avviaSubito, setAvviaSubito] = useState(false);
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramPassword, setTelegramPassword] = useState('');
  const [mercatoScelto, setMercatoScelto] = useState<number | null>(null);
  const [strategiaScelta, setStrategiaScelta] = useState('');
  const [forma, setForma] = useState<FormaRicetta>({
    asset: '',
    currency: '',
    candleSize: '',
    warmupPeriod: '',
  });
  const [bozze, setBozze] = useState<Record<string, Bozza>>({});

  /* catena */
  const [dexScelto, setDexScelto] = useState<Dex>('ZEROX');
  const [catenaScelta, setCatenaScelta] = useState<number | null>(null);
  const [rpc, setRpc] = useState('');
  const [gasLimit, setGasLimit] = useState('');
  const [slippage, setSlippage] = useState('');
  const [minGas, setMinGas] = useState('');
  const [ricaricaGas, setRicaricaGas] = useState('');
  const [esitoRpc, setEsitoRpc] = useState<EsitoProvaRpc | null>(null);
  const [rpcInProva, setRpcInProva] = useState(false);

  /* conferme e invio */
  const [confermaDenaro, setConfermaDenaro] = useState(false);
  const [confermaCreazione, setConfermaCreazione] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [erroreInvio, setErroreInvio] = useState<string | null>(null);

  /* ---------- l'origine: la ricetta del backtest ---------- */

  const datoBacktest = richiestaBacktest.dati;
  const motivoRicetta = datoBacktest ? motivoRicettaInutilizzabile(datoBacktest) : null;

  const origine: Ricetta | null = useMemo(
    () => (datoBacktest && !motivoRicetta ? ricettaDaBacktest(datoBacktest) : null),
    [datoBacktest, motivoRicetta],
  );

  const strategie: StrategiaDisponibile[] = richiestaStrategie.dati ?? [];

  const nomeStrategia = origine?.strategyName ?? strategiaScelta;

  /**
   * L'oggetto da cui si COPIA.
   *
   * Dal backtest quando c'è, altrimenti i predefiniti della strategia scelta.
   * Sono due sorgenti diverse e la differenza va detta: i predefiniti non sono
   * stati misurati da nessuno.
   */
  const paramsOrigine: Record<string, unknown> | null = useMemo(() => {
    if (origine) return origine.strategyParams;
    if (!strategiaScelta) return null;
    return strategie.find((s) => s.name === strategiaScelta)?.defaultParams ?? null;
  }, [origine, strategiaScelta, strategie]);

  const parametri: ParametroRicetta[] = useMemo(
    () => (paramsOrigine ? elencoParametri(paramsOrigine) : []),
    [paramsOrigine],
  );

  /* I campi partono dai valori di partenza, una volta sola per ogni sorgente:
     reinizializzarli a ogni render cancellerebbe quello che si sta scrivendo. */
  useEffect(() => {
    if (!paramsOrigine) {
      setBozze({});
      return;
    }
    const iniziali: Record<string, Bozza> = {};
    for (const p of elencoParametri(paramsOrigine)) iniziali[p.chiave] = bozzaIniziale(p);
    setBozze(iniziali);
  }, [paramsOrigine]);

  useEffect(() => {
    if (!origine) return;
    setForma({
      asset: origine.asset,
      currency: origine.currency,
      candleSize: String(origine.candleSize),
      warmupPeriod: String(origine.warmupPeriod),
    });
  }, [origine]);

  /* ---------- il mercato del segnale ---------- */

  const mercati: MarketWatcher[] = richiestaMercati.dati?.data ?? [];

  /* Si preseleziona il mercato che corrisponde alla coppia, riconosciuta con le
     stesse normalizzazioni del legame bot ↔ backtest: un backtest su BTC/USDT
     trova il mercato BTC/USDC senza farlo cercare a mano. */
  useEffect(() => {
    if (mercatoScelto != null || mercati.length === 0 || !forma.asset) return;
    const a = normalizzaAsset(forma.asset);
    const c = normalizzaValuta(forma.currency);
    const esatto = mercati.find(
      (m) => normalizzaAsset(m.asset) === a && normalizzaValuta(m.currency) === c,
    );
    if (esatto) setMercatoScelto(esatto.id);
  }, [mercati, forma.asset, forma.currency, mercatoScelto]);

  const mercato = mercati.find((m) => m.id === mercatoScelto) ?? null;

  /* ---------- le catene, solo su richiesta ---------- */

  const richiestaCatene = usaRichiesta(() => tokens.catene(), [], {
    attiva: sede === 'wallet',
  });
  const catene: Catena[] = richiestaCatene.dati ?? [];
  const catena = catene.find((c) => c.chainId === catenaScelta) ?? null;

  /* I valori consigliati vengono dal profilo della catena e, se l'RPC scelto ha
     risposto, sono ricalcolati sul gas price reale di quell'endpoint. */
  const suggerimentoGas = esitoRpc?.ok ? esitoRpc.gasSuggestion : catena?.gasDefaults ?? null;

  /* Cambiando catena i campi che valgono per una catena sola vanno azzerati:
     un RPC parla di una catena sola, e un gas limit tarato su BSC non è un gas
     limit per Polygon. */
  useEffect(() => {
    setRpc('');
    setEsitoRpc(null);
    setGasLimit('');
    setMinGas('');
    setRicaricaGas('');
    setBersaglioScelto(null);
  }, [catenaScelta]);

  /* ---------- i bersagli, dallo stesso caricatore della clonazione ---------- */

  const capitale = leggiCapitale(capitaleGrezzo);
  const valuta = forma.currency.trim().toUpperCase();

  /**
   * La configurazione che sta per nascere, nella forma che i bersagli sanno
   * leggere.
   *
   * `bersagliDaConti` e `bersagliDaWallet` ragionano su un bot: chiedono la
   * valuta di quotazione, la catena, e la sede da escludere. Qui il bot non
   * esiste ancora, quindi si passa la configurazione futura con un id di sede
   * che nessun conto ha: così non si esclude nulla, ma la regola di
   * ammissibilità resta la stessa parola per parola.
   */
  const riferimento = useMemo(
    () =>
      ({
        accountId: sede === 'account' ? -1 : null,
        walletId: sede === 'wallet' ? -1 : null,
        currency: valuta,
        chainId: catenaScelta,
      }) as unknown as Tradebot,
    [sede, valuta, catenaScelta],
  );

  const bersagliPronti = valuta !== '' && (sede === 'account' || catenaScelta != null);

  /* La lettura dipende SOLO da dove si cerca: `get-all-balances` e' lenta e
     seriale, e rilanciarla a ogni lettera battuta nel campo della valuta la
     rendeva inservibile. Valuta e catena entrano nella derivazione, che e' un
     calcolo in memoria.

     Non parte finche' la ricetta non e' utilizzabile: interrogare N exchange in
     serie per un backtest che non si potra' comunque portare live e' lavoro
     buttato addosso all'exchange, non solo a noi. */
  const leggibiliBersagli =
    motivoRicetta == null && (!backtestRichiesto || richiestaBacktest.stato === 'pieno');

  const richiestaBersagli = usaRichiesta(
    (segnale) => caricaDatiBersagli({ accountId: null, walletId: sede === 'wallet' ? -1 : null }, segnale),
    [sede],
    { attiva: leggibiliBersagli, eVuoto: (d) => quantiBersagli(d) === 0 },
  );

  const bersagli: Bersaglio[] = useMemo(
    () => (richiestaBersagli.dati ? costruisciBersagli(richiestaBersagli.dati, riferimento) : []),
    [richiestaBersagli.dati, riferimento],
  );
  const bersaglio = bersagli.find((b) => b.id === bersaglioScelto) ?? null;
  const bloccoBersaglio = bersaglio ? motivoBlocco(bersaglio, capitale.valore) : null;
  const saldiStantii = eStantio(richiestaBersagli.letturaA);

  /* La scelta si pota da sola appena smette di essere ammissibile — capitale
     alzato, valuta cambiata, saldi riletti. Lasciarla appesa darebbe un
     riepilogo che promette un bot che non nascerà, ed è il difetto da cui
     `bersagli.ts` è nato. */
  useEffect(() => {
    if (bersaglioScelto == null) return;
    const scelto = bersagli.find((b) => b.id === bersaglioScelto);
    if (!scelto || motivoBlocco(scelto, capitale.valore) != null) setBersaglioScelto(null);
  }, [bersagli, bersaglioScelto, capitale.valore]);

  /* ---------- la copia dei parametri ---------- */

  const copia = useMemo(
    () =>
      paramsOrigine
        ? copiaParametri(paramsOrigine, parametri, bozze)
        : { valori: {}, toccate: [], invalide: [], anomalie: [] },
    [paramsOrigine, parametri, bozze],
  );

  /* ---------- warm-up ---------- */

  /* La stima si calcola sui parametri che il bot USERÀ, non su quelli del
     backtest: se si cambia `N1` o `r`, la finestra più lunga cambia con loro e
     il minimo di prima non è più il minimo. */
  const stima = useMemo(
    () => (paramsOrigine && nomeStrategia ? stimaWarmup(nomeStrategia, copia.valori) : null),
    [nomeStrategia, paramsOrigine, copia.valori],
  );

  const warmup = Number(forma.warmupPeriod);
  const warmupValido = Number.isFinite(warmup) && warmup > 0;
  const warmupSotto = stima != null && warmupValido && warmup < stima.minimo;

  const scostamenti: Scostamento[] = useMemo(
    () => (origine ? scostamentiRicetta(origine, forma, parametri, bozze) : []),
    [origine, forma, parametri, bozze],
  );

  /* ---------- validazione ---------- */

  const candela = Number(forma.candleSize);
  const configurazioneDex: ConfigurazioneDex | null =
    sede === 'wallet' && catenaScelta != null
      ? {
          dex: dexScelto,
          chainId: catenaScelta as ConfigurazioneDex['chainId'],
          rpc,
          gasLimit,
          /* Vuoto significa «il predefinito di questo DEX», che su Velora non è
             quello del backend: 0,5% su un costo reale di 2 punti base non è una
             tolleranza, è un invito al sandwich. */
          swapSlippage:
            slippage.trim() !== ''
              ? slippage
              : SLIPPAGE_PREDEFINITO[dexScelto] == null
                ? ''
                : String(SLIPPAGE_PREDEFINITO[dexScelto]),
          minimumBaseCoinBalance: minGas,
          rechargeQtyBaseCoinBalance: ricaricaGas,
        }
      : null;

  const motivoPulsante = ((): string | null => {
    if (backtestRichiesto && idBacktest == null) {
      return `L’indirizzo porta un identificativo di backtest non valido (“${grezzoBacktest}”): non c’è nessuna configurazione da copiare.`;
    }
    if (backtestRichiesto && richiestaBacktest.stato !== 'pieno') {
      return 'Il backtest di partenza non è stato letto: non si crea un bot da una ricetta che non si è potuta leggere.';
    }
    if (motivoRicetta) return motivoRicetta;
    if (!nomeStrategia) return 'Scegli la strategia.';
    if (!paramsOrigine) return 'I parametri della strategia non sono disponibili.';
    if (copia.anomalie.length > 0) {
      return `La copia dei parametri non è integra (${copia.anomalie.join('; ')}). Il bot non viene creato.`;
    }
    if (copia.invalide.length > 0) {
      return copia.invalide.length === 1
        ? `Il parametro «${copia.invalide[0]}» non contiene un valore utilizzabile.`
        : `${copia.invalide.length} parametri non contengono un valore utilizzabile.`;
    }
    if (!forma.asset.trim() || !forma.currency.trim()) return 'Indica asset e valuta di quotazione.';
    if (!Number.isFinite(candela) || candela <= 0) return 'La candela deve essere un numero di minuti maggiore di zero.';
    if (!warmupValido) return 'Il warm-up deve essere un numero di candele maggiore di zero.';
    /* L'unico caso in cui il warm-up ferma la creazione. Alzarlo è spreco,
       abbassarlo entro il minimo è legittimo; scendere SOTTO il minimo produce
       un bot che decide su indicatori non formati e un risultato che sembra
       migliore del vero — un errore che non si vede mai da solo. */
    if (warmupSotto && stima) {
      return `Il warm-up è sotto il minimo stimato di ${num(stima.minimo)} candele: gli indicatori non sarebbero formati alla prima decisione, e il bot darebbe un risultato che sembra migliore di quello vero.`;
    }
    if (mercatoScelto == null) return 'Scegli il mercato da cui arrivano le candele del segnale.';
    if (!capitale.valido) return capitale.motivo;
    if (sede === 'wallet' && catenaScelta == null) return 'Scegli la catena su cui il bot opera.';
    if (sede === 'wallet') {
      if (!rpc.trim()) return 'Indica l’RPC con cui il bot parla con la catena.';
      if (esitoRpc && !esitoRpc.chainIdMatches && esitoRpc.chainId != null) {
        return `L’RPC risponde per la catena ${esitoRpc.chainId}, non per la ${catenaScelta}: il bot firmerebbe sulla catena sbagliata.`;
      }
      if (!Number.isFinite(Number(gasLimit)) || Number(gasLimit) <= 0) return 'Indica il limite di gas per transazione.';
      if (!Number.isFinite(Number(minGas)) || Number(minGas) <= 0) return 'Indica la riserva minima di gas.';
      if (!Number.isFinite(Number(ricaricaGas)) || Number(ricaricaGas) <= 0) return 'Indica la quantità di gas da ricomprare a ogni ricarica.';
    }
    if (bersaglioScelto == null) {
      return sede === 'account'
        ? 'Scegli il conto exchange su cui il bot opera.'
        : 'Scegli il wallet su cui il bot opera.';
    }
    if (bloccoBersaglio) return bloccoBersaglio;
    return null;
  })();

  /* ---------- azioni ---------- */

  async function provaRpc() {
    if (!rpc.trim() || catenaScelta == null || rpcInProva) return;
    setRpcInProva(true);
    try {
      const esito = await tokens.provaRpc(rpc.trim(), catenaScelta);
      setEsitoRpc(esito);
      /* Se l'endpoint risponde, i valori di gas si ricalcolano sul suo gas price
         reale: quelli del profilo di catena sono una media, questi sono adesso. */
      if (esito.ok && esito.gasSuggestion) {
        if (!gasLimit) setGasLimit(String(esito.gasSuggestion.gasLimit));
        if (!minGas) setMinGas(String(esito.gasSuggestion.minimumBaseCoinBalance));
        if (!ricaricaGas) setRicaricaGas(String(esito.gasSuggestion.rechargeQtyBaseCoinBalance));
      }
    } catch (e) {
      setEsitoRpc(null);
      mostra(e instanceof Error ? e.message : 'Prova dell’RPC non riuscita', 'fallito');
    } finally {
      setRpcInProva(false);
    }
  }

  async function crea() {
    if (motivoPulsante || inCorso || bersaglio == null || mercatoScelto == null) return;
    setInCorso(true);
    setErroreInvio(null);

    try {
      const corpo = corpoCreazione({
        strategyName: nomeStrategia,
        /* L'oggetto verificato da `copiaParametri`, non una ricostruzione. */
        strategyParams: copia.valori,
        asset: forma.asset,
        currency: forma.currency,
        candleSize: candela,
        warmupPeriod: warmup,
        marketWatcherId: mercatoScelto,
        sede: { tipo: bersaglio.tipo, id: bersaglio.id },
        capitale: capitale.valore,
        usePartialBalance: saldoParziale,
        useRealMoney: denaroVero,
        avviaSubito,
        telegramToken,
        telegramPassword,
        dex: configurazioneDex,
      });

      const nato = await tradebots.crea(corpo);
      setConfermaCreazione(false);
      /* Si va sul dettaglio del bot appena nato, e non si mostra un avviso: la
         schermata che si apre dice lo stato vero — fermo o in marcia, reale o
         simulato — mentre un avviso su una pagina che si sta lasciando non lo
         leggerebbe nessuno. */
      naviga(`/tradebot/${nato.id}`);
    } catch (e) {
      setErroreInvio(e instanceof Error ? e.message : 'Creazione non riuscita.');
    } finally {
      setInCorso(false);
    }
  }

  /* ==========================================================================
   * Resa
   * ======================================================================== */

  /* Il backtest è il presupposto di tutto ciò che sta sotto: finché non è
     arrivato non si mostra un modulo precompilato a metà, che si riempirebbe
     sotto le mani mentre lo si compila. */
  const statoOrigine =
    backtestRichiesto && idBacktest != null ? richiestaBacktest.stato : 'pieno';

  /* Il modulo non compare quando la ricetta non c'è o non si può usare.
     Mostrarlo comunque, con la strategia da riscegliere a mano, offrirebbe di
     ricostruire d'istinto una configurazione che era stata misurata: è il modo
     più diretto di far nascere un bot che si crede un altro. */
  const ricettaBloccata = motivoRicetta != null || (backtestRichiesto && idBacktest == null);

  return (
    <div className="sb-cornice">
      <IntestazionePagina
        titolo="Nuovo tradebot"
        sotto={
          <span className="sb-nota">
            {backtestRichiesto
              ? idBacktest == null
                ? 'indirizzo non valido'
                : `dal backtest #${idBacktest}`
              : 'da zero, senza un backtest alle spalle'}
          </span>
        }
        azioni={
          <Pulsante onClick={() => naviga('/tradebot')}>Torna ai bot</Pulsante>
        }
      />

      <ContenitoreDati
        stato={statoOrigine}
        causaVuoto={`Nessun backtest con identificativo ${idBacktest}. Può essere stato eliminato, oppure l’indirizzo è vecchio.`}
        azioneVuoto={{ etichetta: 'Vai ai backtest', onClick: () => naviga('/backtest') }}
        messaggioErrore={richiestaBacktest.errore?.message}
        onRiprova={richiestaBacktest.ricarica}
      >
        <div className="tb-nuovo">
          {backtestRichiesto && idBacktest == null && (
            <Avvertenza livello="grave">
              L’indirizzo chiede di partire dal backtest «{grezzoBacktest}», che non è un
              identificativo valido. Non c’è nessuna configurazione da copiare: torna al backtest e
              ripremi «Crea tradebot».
            </Avvertenza>
          )}

          {/* ================= 1. provenienza ================= */}
          {datoBacktest && (
            <Provenienza
              dato={datoBacktest}
              motivo={motivoRicetta}
              onApri={() => naviga(`/backtest/${datoBacktest.id}`)}
            />
          )}

          {!backtestRichiesto && (
            <Avvertenza livello="cautela">
              Questo bot non nasce da un backtest: <b>nessuna misura sostiene questi parametri</b>.
              I valori proposti sono i predefiniti della strategia, non una configurazione
              selezionata. Se una misura esiste, conviene partire da quella — dal dettaglio del
              backtest, col pulsante «Crea tradebot».
            </Avvertenza>
          )}

          {motivoRicetta && (
            <Avvertenza livello="grave">
              <b>Da questo backtest non si crea un bot.</b> {motivoRicetta} Non c’è un campo da
              compilare al posto suo: ricostruire i parametri a mano farebbe nascere un bot che si
              crede quello misurato e opera come una strategia che nessuno ha provato. Scegli un
              altro backtest, o rilancia questo perché li salvi.
            </Avvertenza>
          )}

          {!ricettaBloccata && (
            <>
              {/* ================= 2. la ricetta ================= */}
              <Scheda>
                <Occhiello>La ricetta — cosa farà il bot</Occhiello>
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                  {origine
                    ? `Questi valori vengono dal backtest #${datoBacktest?.id}. Cambiarne uno stacca il bot da quella misura, tranne il warm-up: quello si può ridurre.`
                    : 'Questi valori li scegli tu: non c’è un backtest che li abbia misurati.'}
                </div>

                <div className="tb-nuovo__griglia" style={{ marginTop: 'var(--sb-s-6)' }}>
                  {origine ? (
                    <div className="tb-voce" style={{ alignItems: 'center' }}>
                      <span className="tb-voce__chiave">Strategia</span>
                      <span className="tb-voce__valore">{origine.strategyName}</span>
                    </div>
                  ) : (
                    <Selettore
                      etichetta="Strategia"
                      suggerimento={`${strategie.length} disponibili`}
                      valore={strategiaScelta}
                      voci={[
                        { valore: '', etichetta: '— scegli —' },
                        ...strategie.map((s) => ({ valore: s.name, etichetta: s.name })),
                      ]}
                      onCambia={setStrategiaScelta}
                    />
                  )}

                  <CampoRicetta
                    etichetta="Asset"
                    valore={forma.asset}
                    onCambia={(v) => setForma((f) => ({ ...f, asset: v }))}
                    scostamento={scostamenti.find((s) => s.chiave === 'asset')}
                    testuale
                  />
                  <CampoRicetta
                    etichetta="Valuta di quotazione"
                    valore={forma.currency}
                    onCambia={(v) => setForma((f) => ({ ...f, currency: v }))}
                    scostamento={scostamenti.find((s) => s.chiave === 'currency')}
                    testuale
                  />
                  <CampoRicetta
                    etichetta="Candela"
                    suggerimento="in minuti"
                    unita="min"
                    valore={forma.candleSize}
                    onCambia={(v) => setForma((f) => ({ ...f, candleSize: v }))}
                    scostamento={scostamenti.find((s) => s.chiave === 'candleSize')}
                  />
                </div>

                {origine && sede === 'wallet' && (
                  <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
                    Su catena l’asset e la valuta sono i ticker on-chain, che spesso non sono quelli
                    del backtest: <code>BTCB</code> su BSC è il <code>BTC</code> misurato,{' '}
                    <code>USDC</code> è la <code>USDT</code>. Un cambio di questo genere è segnalato
                    come <b>nominale</b> e non stacca il bot dalla misura.
                  </div>
                )}
              </Scheda>

              {/* ================= 3. il warm-up ================= */}
              <Scheda>
                <Occhiello>Warm-up</Occhiello>
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                  Quante candele passate il bot consuma all’avvio prima di decidere. Nei backtest è
                  spesso gonfiato apposta per allineare timeframe diversi — quelli a un’ora ne
                  portano quattro volte quelli a quattro ore, così le due misure partono dallo stesso
                  punto della finestra reale. Per un bot dal vivo quel gonfiaggio non serve: serve
                  solo che gli indicatori siano formati.
                </div>

                <div style={{ marginTop: 'var(--sb-s-5)', maxWidth: 320 }}>
                  <Campo
                    etichetta="Candele di warm-up"
                    suggerimento={stima ? `minimo stimato ${num(stima.minimo)}` : 'minimo non stimabile'}
                    type="number"
                    min={1}
                    step="1"
                    inputMode="numeric"
                    unita="candele"
                    value={forma.warmupPeriod}
                    onChange={(e) => setForma((f) => ({ ...f, warmupPeriod: e.target.value }))}
                    errore={
                      forma.warmupPeriod !== '' && !warmupValido
                        ? 'Serve un numero di candele maggiore di zero.'
                        : null
                    }
                  />
                </div>

                {stima && (
                  <div style={{ marginTop: 'var(--sb-s-4)' }}>
                    <Pulsante
                      variante="tenue"
                      onClick={() => setForma((f) => ({ ...f, warmupPeriod: String(stima.consigliato) }))}
                    >
                      Usa il consigliato: {num(stima.consigliato)}
                    </Pulsante>
                    <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                      Per {nomeStrategia}, {stima.formula}.
                    </div>
                  </div>
                )}

                {!stima && nomeStrategia && (
                  <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                    Non sappiamo dedurre il warm-up minimo di {nomeStrategia}: il vincolo dipende da
                    quali finestre usa, e indovinarlo autorizzerebbe a scendere sotto la soglia vera.
                    {origine
                      ? ` Resta quello del backtest (${num(origine.warmupPeriod)} candele), che è misurato.`
                      : ' Scegli un valore che copra con margine la finestra più lunga della strategia.'}
                  </div>
                )}

                {warmupSotto && stima && (
                  <Avvertenza livello="grave">
                    <b>Warm-up sotto il minimo.</b> Con {num(warmup)} candele invece di{' '}
                    {num(stima.minimo)} gli indicatori non sono formati quando il bot prende la prima
                    decisione. Non dà errore: dà un risultato che <b>sembra migliore</b> e non lo è —
                    misurato, lo stesso backtest con 2.400 candele invece di 2.695 rendeva 12,56%
                    invece di 10,57%.
                  </Avvertenza>
                )}

                {stima && warmupValido && warmup > stima.consigliato * 2 && (
                  <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                    Molto sopra il consigliato: non è un pericolo, sono solo candele in più da
                    scaricare — il bot impiegherà qualche secondo in più ad avviarsi.
                  </div>
                )}
              </Scheda>

              {/* ================= 4. i parametri, tutti ================= */}
              <ParametriStrategia
                parametri={parametri}
                bozze={bozze}
                daBacktest={origine != null}
                idBacktest={datoBacktest?.id ?? null}
                onCambia={(chiave, v) => setBozze((b) => ({ ...b, [chiave]: v }))}
                onRipristina={(p) => setBozze((b) => ({ ...b, [p.chiave]: bozzaIniziale(p) }))}
              />

              {/* ================= 5. gli scostamenti ================= */}
              {origine && scostamenti.length > 0 && (
                <RiepilogoScostamenti
                  scostamenti={scostamenti}
                  idBacktest={datoBacktest?.id ?? null}
                />
              )}

              {copia.anomalie.length > 0 && (
                <Avvertenza livello="grave">
                  <b>La copia dei parametri non è integra.</b>{' '}
                  {copia.anomalie.map((a) => a).join('; ')}. Il bot non viene creato: è un difetto
                  dell’interfaccia, non una scelta da confermare.
                </Avvertenza>
              )}

              {/* ================= 6. il segnale ================= */}
              <SegnaleMercato
                mercati={mercati}
                stato={richiestaMercati.stato}
                errore={richiestaMercati.errore?.message}
                scelto={mercatoScelto}
                onScegli={setMercatoScelto}
                onRicarica={richiestaMercati.ricarica}
                asset={forma.asset}
                currency={forma.currency}
                exchangeIdSuggerito={datoBacktest?.exchangeId ?? null}
                onCreato={(id) => {
                  setMercatoScelto(id);
                  richiestaMercati.ricarica();
                  mostra('Mercato del segnale creato');
                }}
              />

              {/* ================= 7. il capitale ================= */}
              <Scheda>
                <Occhiello>Capitale</Occhiello>
                {/* Il capitale è un TETTO solo sui conti di exchange. Su un wallet no, e non è
                    una mancanza: un bot su catena impegna tutto il saldo dell'indirizzo, e per
                    limitarne l'esposizione si crea un wallet dedicato — su una catena un secondo
                    indirizzo non costa niente. Dichiarare un tetto dove non esiste è peggio che
                    non offrirlo, perché chi scrive 500 su un wallet da 10.000 crederebbe di
                    rischiare 500. */}
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                  {sede === 'wallet'
                    ? 'Con quanto nasce il bot. Su un wallet non è un tetto: il bot impegna tutto il saldo dell’indirizzo.'
                    : 'Su un conto di exchange è un tetto: il bot non impegna più di questa cifra, e più bot possono lavorare sullo stesso conto.'}
                </div>
                {sede === 'wallet' && (
                  <Avvertenza livello="cautela">
                    Per limitare quanto questo bot può muovere, usa un <b>wallet dedicato</b> con
                    dentro solo il capitale che vuoi rischiare. Sul percorso su catena il capitale
                    indicato qui non viene fatto rispettare.
                  </Avvertenza>
                )}

                <div style={{ marginTop: 'var(--sb-s-5)', maxWidth: 320 }}>
                  <Campo
                    etichetta={`Capitale iniziale${valuta ? ` in ${valuta}` : ''}`}
                    suggerimento={`minimo ${num(CAPITALE_MINIMO)}`}
                    type="number"
                    min={CAPITALE_MINIMO}
                    step="any"
                    inputMode="decimal"
                    unita={valuta || undefined}
                    value={capitaleGrezzo}
                    onChange={(e) => setCapitaleGrezzo(e.target.value)}
                    errore={capitaleGrezzo !== '' && !capitale.valido ? capitale.motivo : null}
                  />
                </div>

                {/* L'interruttore esiste solo per gli exchange: su catena quel flag non viene
                    letto da nessuna parte, e offrirlo sarebbe un comando che non comanda. */}
                {sede !== 'wallet' && (
                  <div style={{ marginTop: 'var(--sb-s-5)' }}>
                    <Interruttore
                      etichetta={
                        saldoParziale
                          ? 'Usa solo il capitale indicato'
                          : 'Usa tutto il saldo del conto'
                      }
                      acceso={saldoParziale}
                      onCambia={() => setSaldoParziale((v) => !v)}
                    />
                  </div>
                )}

                {!saldoParziale && (
                  <Avvertenza livello="cautela">
                    Con <code>usePartialBalance</code> spento il bot muove <b>tutto</b> il saldo
                    della valuta di quotazione sul conto, non i {capitale.valido ? num(capitale.valore, 2) : '—'}{' '}
                    indicati qui sopra. Il capitale iniziale resta scritto sulla riga, ma non è un
                    tetto.
                  </Avvertenza>
                )}
              </Scheda>

              {/* ================= 8. dove opera ================= */}
              <Scheda>
                <Occhiello>Dove opera</Occhiello>

                <div className="tb-filtri" role="group" aria-label="Tipo di sede" style={{ marginTop: 'var(--sb-s-5)' }}>
                  <button
                    type="button"
                    aria-pressed={sede === 'account'}
                    className={`tb-filtro${sede === 'account' ? ' tb-filtro--attivo' : ''}`}
                    onClick={() => {
                      setSede('account');
                      setBersaglioScelto(null);
                    }}
                  >
                    Conto exchange
                  </button>
                  <button
                    type="button"
                    aria-pressed={sede === 'wallet'}
                    className={`tb-filtro${sede === 'wallet' ? ' tb-filtro--attivo' : ''}`}
                    onClick={() => {
                      setSede('wallet');
                      setBersaglioScelto(null);
                    }}
                  >
                    Wallet su catena
                  </button>
                </div>

                {sede === 'wallet' && (
                  <SezioneCatena
                    catene={catene}
                    stato={richiestaCatene.stato}
                    errore={richiestaCatene.errore?.message}
                    onRicarica={richiestaCatene.ricarica}
                    catenaScelta={catenaScelta}
                    onCatena={setCatenaScelta}
                    catena={catena}
                    dexScelto={dexScelto}
                    onDex={setDexScelto}
                    rpc={rpc}
                    onRpc={(v) => {
                      setRpc(v);
                      setEsitoRpc(null);
                    }}
                    esitoRpc={esitoRpc}
                    inProva={rpcInProva}
                    onProva={provaRpc}
                    gasLimit={gasLimit}
                    onGasLimit={setGasLimit}
                    minGas={minGas}
                    onMinGas={setMinGas}
                    ricaricaGas={ricaricaGas}
                    onRicaricaGas={setRicaricaGas}
                    slippage={slippage}
                    onSlippage={setSlippage}
                    suggerimento={suggerimentoGas}
                  />
                )}

                {/* --- i bersagli --- */}
                <div style={{ marginTop: 'var(--sb-s-7)' }}>
                  <Occhiello>{sede === 'account' ? 'Conti' : 'Wallet'}</Occhiello>

                  {!bersagliPronti ? (
                    <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                      {valuta === ''
                        ? 'Serve prima la valuta di quotazione: il saldo libero si legge in quella valuta, non in dollari.'
                        : 'Serve prima la catena: il saldo e il gas si leggono su una catena sola.'}
                    </div>
                  ) : (
                    <>
                      {saldiStantii && richiestaBersagli.letturaA && (
                        <Avvertenza livello="cautela">
                          I saldi sono di prima, letti alle {ora(richiestaBersagli.letturaA)}.
                          Rileggili prima di impegnare capitale.{' '}
                          <button
                            type="button"
                            onClick={richiestaBersagli.ricarica}
                            style={{
                              all: 'unset',
                              cursor: 'pointer',
                              color: 'var(--sb-accento-chiaro)',
                              textDecoration: 'underline',
                              minHeight: 'var(--sb-tocco-min)',
                            }}
                          >
                            Rileggi ora
                          </button>
                        </Avvertenza>
                      )}

                      <div style={{ marginTop: 'var(--sb-s-4)' }}>
                        <ContenitoreDati
                          stato={
                            richiestaBersagli.inAggiornamento ? 'caricamento' : richiestaBersagli.stato
                          }
                          causaVuoto={
                            sede === 'account'
                              ? 'Non c’è nessun conto exchange registrato: si aggiunge da Impostazioni › Account.'
                              : 'Non c’è nessun wallet registrato: si aggiunge dalla schermata Wallet.'
                          }
                          messaggioErrore={richiestaBersagli.errore?.message}
                          onRiprova={richiestaBersagli.ricarica}
                        >
                          <div className="tb-bersagli" role="radiogroup" aria-label="Sede del bot">
                            {bersagli.map((b) => (
                              <RigaBersaglio
                                key={`${b.tipo}-${b.id}`}
                                bersaglio={b}
                                capitale={capitale}
                                scelto={bersaglioScelto === b.id}
                                singola
                                onCambia={() =>
                                  setBersaglioScelto((s) => (s === b.id ? null : b.id))
                                }
                              />
                            ))}
                          </div>
                        </ContenitoreDati>
                      </div>

                      {(richiestaBersagli.dati?.troncato ?? 0) > 0 && (
                        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                          Altri {richiestaBersagli.dati?.troncato} wallet non sono in elenco: il
                          riepilogo dei saldi ne accetta al massimo {MAX_WALLET} per richiesta.
                        </div>
                      )}

                      {richiestaBersagli.dati?.saldiMuti && (
                        <Avvertenza livello="grave">
                          I saldi non si sono potuti leggere. I conti restano a schermo bloccati col
                          motivo: non si impegna capitale su un saldo che non si è verificato.
                        </Avvertenza>
                      )}
                    </>
                  )}
                </div>
              </Scheda>

              {/* ================= 9. telegram ================= */}
              <Scheda>
                <Occhiello>Notifiche Telegram — facoltative</Occhiello>
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                  Servono al bot per avvisare a ogni operazione. Se li lasci vuoti il bot opera in
                  silenzio: nessuna notifica, nemmeno sugli errori.
                </div>
                <div className="tb-nuovo__griglia" style={{ marginTop: 'var(--sb-s-5)' }}>
                  <Campo
                    etichetta="Token del bot Telegram"
                    testuale
                    autoComplete="off"
                    value={telegramToken}
                    onChange={(e) => setTelegramToken(e.target.value)}
                  />
                  <Campo
                    etichetta="Password di iscrizione"
                    testuale
                    autoComplete="off"
                    value={telegramPassword}
                    onChange={(e) => setTelegramPassword(e.target.value)}
                  />
                </div>
              </Scheda>

              {/* ================= 10. denaro vero, da solo ================= */}
              <SezioneDenaro
                denaroVero={denaroVero}
                capitale={capitale}
                valuta={valuta}
                nomeSede={bersaglio?.nome ?? null}
                onSpegni={() => setDenaroVero(false)}
                onChiediAccensione={() => setConfermaDenaro(true)}
              />

              {/* ================= 11. avvio e creazione ================= */}
              <Scheda>
                <Occhiello>Alla creazione</Occhiello>
                <div style={{ marginTop: 'var(--sb-s-5)' }}>
                  <Interruttore
                    etichetta={avviaSubito ? 'Avvia subito' : 'Crea fermo'}
                    acceso={avviaSubito}
                    onCambia={() => setAvviaSubito((v) => !v)}
                  />
                </div>
                <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
                  {avviaSubito
                    ? 'Il bot parte appena creato: consuma il warm-up in pochi secondi e può prendere posizione alla prima candela utile.'
                    : 'Il bot nasce fermo. Lo avvii tu dal suo dettaglio, quando vuoi.'}
                </div>

                <div className="sb-riga" style={{ marginTop: 'var(--sb-s-7)', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Pulsante
                      variante={denaroVero ? 'cautela' : 'primario'}
                      largo
                      disabled={!!motivoPulsante || inCorso}
                      onClick={() => setConfermaCreazione(true)}
                    >
                      {denaroVero
                        ? `Crea con denaro vero · ${num(capitale.valore, 2)} ${valuta}`
                        : 'Crea il bot in simulazione'}
                    </Pulsante>
                    {motivoPulsante && <span className="sb-motivo-blocco">{motivoPulsante}</span>}
                  </div>
                </div>

                {erroreInvio && <Avvertenza livello="grave">{erroreInvio}</Avvertenza>}
              </Scheda>
            </>
          )}
        </div>
      </ContenitoreDati>

      {/* ---------- conferma: passare a denaro vero ---------- */}
      <Modale
        aperta={confermaDenaro}
        onChiudi={() => setConfermaDenaro(false)}
        titolo="Denaro vero"
        sottotitolo="il bot comprerà e venderà davvero"
        pieda={
          <>
            <Pulsante onClick={() => setConfermaDenaro(false)}>Resta in simulazione</Pulsante>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Pulsante
                variante="cautela"
                largo
                disabled={!capitale.valido}
                onClick={() => {
                  setDenaroVero(true);
                  setConfermaDenaro(false);
                }}
              >
                Sì, {num(capitale.valore, 2)} {valuta} veri
              </Pulsante>
              {!capitale.valido && <span className="sb-motivo-blocco">{capitale.motivo}</span>}
            </div>
          </>
        }
      >
        <Avvertenza livello="cautela">
          Stai per impegnare{' '}
          <b>
            {capitale.valido ? num(capitale.valore, 2) : '—'} {valuta}
          </b>{' '}
          di capitale reale{bersaglio ? ` su «${bersaglio.nome}»` : ''}. Non è una prova: il bot
          invia ordini veri, paga commissioni vere e può chiudere in perdita.
        </Avvertenza>
        <div className="sb-nota">
          In simulazione il bot fa esattamente gli stessi calcoli e scrive gli stessi numeri, senza
          mandare ordini. È il modo di verificare che parta e che decida come ti aspetti, prima di
          dargli il capitale.
        </div>
      </Modale>

      {/* ---------- conferma: creazione ---------- */}
      <Modale
        aperta={confermaCreazione}
        onChiudi={() => setConfermaCreazione(false)}
        titolo={denaroVero ? 'Creare il bot con denaro vero?' : 'Creare il bot in simulazione?'}
        larga
        pieda={
          <>
            <Pulsante onClick={() => setConfermaCreazione(false)} disabled={inCorso}>
              Annulla
            </Pulsante>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Pulsante
                variante={denaroVero ? 'cautela' : 'primario'}
                largo
                disabled={!!motivoPulsante || inCorso}
                onClick={crea}
              >
                {inCorso
                  ? 'Creazione in corso…'
                  : denaroVero
                    ? `Conferma · ${num(capitale.valore, 2)} ${valuta} veri`
                    : 'Conferma · simulato'}
              </Pulsante>
              {motivoPulsante && <span className="sb-motivo-blocco">{motivoPulsante}</span>}
            </div>
          </>
        }
      >
        <Riepilogo
          strategia={nomeStrategia}
          asset={forma.asset}
          currency={forma.currency}
          candela={candela}
          warmup={warmup}
          quantiParametri={parametri.length}
          scostamenti={scostamenti}
          idBacktest={origine ? (datoBacktest?.id ?? null) : null}
          bersaglio={bersaglio}
          mercato={mercato}
          capitale={capitale.valore}
          valuta={valuta}
          saldoParziale={saldoParziale}
          denaroVero={denaroVero}
          avviaSubito={avviaSubito}
          conTelegram={telegramToken.trim() !== ''}
          dex={configurazioneDex}
          nomeCatena={catena?.name ?? null}
        />
        {erroreInvio && <Avvertenza livello="grave">{erroreInvio}</Avvertenza>}
      </Modale>

      <Avvisi avvisi={avvisi} />
    </div>
  );
}

/* ==========================================================================
 * Provenienza: il backtest da cui si parte
 * ======================================================================== */

function Provenienza({
  dato,
  motivo,
  onApri,
}: {
  dato: Backtest;
  motivo: string | null;
  onApri: () => void;
}) {
  const quanti = dato.strategyParams ? Object.keys(dato.strategyParams).length : 0;

  return (
    <Scheda variante="incassata">
      <div className="tb-testata">
        <IconaStrategia nome={dato.strategyName} dim={34} />
        <div className="tb-testata__corpo">
          <div className="tb-testata__titolo">
            <span className="tb-coppia">
              {dato.asset}/{dato.currency}
            </span>
            <Distintivo contorno>{timeframe(dato.candleSize)}</Distintivo>
            {motivo == null && (
              <Distintivo tono="accento">
                {quanti === 1 ? '1 parametro' : `${quanti} parametri`}
              </Distintivo>
            )}
          </div>
          <div className="tb-sotto">
            {dato.strategyName} · backtest #{dato.id}
          </div>
          <div style={{ display: 'flex', gap: 'var(--sb-s-3)', flexWrap: 'wrap', marginTop: 'var(--sb-s-4)' }}>
            <DistintivoPeriodo>
              {fmtPeriodo(dato.startDate, dato.endDate)}
              {durataPeriodo(dato.startDate, dato.endDate)
                ? ` · ${durataPeriodo(dato.startDate, dato.endDate)}`
                : ''}
            </DistintivoPeriodo>
          </div>
        </div>
        <Pulsante onClick={onApri}>Apri il backtest</Pulsante>
      </div>

      <div className="tb-tre" style={{ marginTop: 'var(--sb-s-6)' }}>
        <div>
          <div className="tb-tre__etichetta">Profitto misurato</div>
          <div className={`tb-tre__valore sb-tono-${tonoRendimento(dato.strategyChange)}`}>
            {dato.strategyChange == null ? 'n/d' : pct(dato.strategyChange, 2)}
          </div>
        </div>
        <div>
          <div className="tb-tre__etichetta">Drawdown</div>
          <div className="tb-tre__valore sb-tono-perdita">
            {dato.drawdownMaxValue == null ? 'n/d' : fmtDrawdown(dato.drawdownMaxValue)}
          </div>
        </div>
        <div>
          <div className="tb-tre__etichetta">Operazioni</div>
          <div className="tb-tre__valore sb-tono-neutro">{num(dato.tradesNumber)}</div>
        </div>
      </div>

      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
        Sono i numeri di <b>quel</b> periodo con <b>quei</b> parametri. Valgono per questo bot solo
        finché la configurazione resta identica.
      </div>
    </Scheda>
  );
}

/* ==========================================================================
 * Un campo della ricetta, col confronto col backtest accanto
 * ======================================================================== */

/**
 * Il campo e la sua differenza dal backtest, sulla stessa riga.
 *
 * La differenza sta ACCANTO al campo e non in un riepilogo lontano: chi cambia
 * un valore deve vedere subito da cosa lo sta sostituendo, non scoprirlo in
 * fondo alla pagina quando ha già cambiato altre tre cose.
 */
function CampoRicetta({
  etichetta,
  suggerimento,
  unita,
  valore,
  onCambia,
  scostamento,
  testuale = false,
}: {
  etichetta: string;
  suggerimento?: string;
  unita?: string;
  valore: string;
  onCambia: (v: string) => void;
  scostamento?: Scostamento;
  testuale?: boolean;
}) {
  return (
    <div>
      <Campo
        etichetta={etichetta}
        suggerimento={suggerimento}
        testuale={testuale}
        type={testuale ? 'text' : 'number'}
        inputMode={testuale ? undefined : 'decimal'}
        step={testuale ? undefined : 'any'}
        unita={unita}
        autoComplete="off"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
      />
      {scostamento && (
        <div className="sb-riga" style={{ marginTop: 'var(--sb-s-3)', gap: 'var(--sb-s-3)' }}>
          <Distintivo tono={scostamento.soloNominale ? 'neutro' : 'cautela'}>
            {scostamento.soloNominale ? 'stesso valore, altro ticker' : 'diverso dal backtest'}
          </Distintivo>
          <span className="tb-transizione">
            <span className="tb-transizione__da tb-transizione__da--sostituito">
              {scostamento.da}
            </span>
            <span className="tb-transizione__freccia" aria-hidden="true">
              →
            </span>
            <span
              className={
                scostamento.soloNominale
                  ? 'tb-transizione__a'
                  : 'tb-transizione__a tb-transizione__a--cambiato'
              }
            >
              {scostamento.a}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * I parametri della strategia — tutti a schermo
 * ======================================================================== */

function ParametriStrategia({
  parametri,
  bozze,
  daBacktest,
  idBacktest,
  onCambia,
  onRipristina,
}: {
  parametri: ParametroRicetta[];
  bozze: Record<string, Bozza>;
  daBacktest: boolean;
  idBacktest: number | null;
  onCambia: (chiave: string, valore: Bozza) => void;
  onRipristina: (p: ParametroRicetta) => void;
}) {
  const cambiati = parametri.filter((p) => eDiverso(p, bozze[p.chiave])).length;

  return (
    <Scheda>
      <div className="tb-parametro__testata">
        <Occhiello>
          Parametri della strategia — {parametri.length === 1 ? '1 valore' : `${parametri.length} valori`}
        </Occhiello>
        {cambiati > 0 && (
          <Distintivo tono="cautela">
            {cambiati === 1 ? '1 modificato' : `${cambiati} modificati`}
          </Distintivo>
        )}
      </div>

      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        {daBacktest
          ? `Sono tutti a schermo, aperti, e vengono dal backtest #${idBacktest} così come sono: nessun filtro, nessun valore predefinito applicato sopra. Confrontali con il dettaglio del backtest prima di creare — è l’unico momento in cui un errore si vede.`
          : 'Sono i valori predefiniti della strategia. Nessuno li ha misurati su un periodo reale.'}
      </div>

      {parametri.length === 0 ? (
        <Avvertenza livello="nota">
          Nessun parametro da mostrare: scegli prima la strategia.
        </Avvertenza>
      ) : (
        <div className="tb-nuovo__parametri" style={{ marginTop: 'var(--sb-s-6)' }}>
          {parametri.map((p) => (
            <RigaParametro
              key={p.chiave}
              parametro={p}
              bozza={bozze[p.chiave]}
              onCambia={(v) => onCambia(p.chiave, v)}
              onRipristina={() => onRipristina(p)}
            />
          ))}
        </div>
      )}
    </Scheda>
  );
}

function RigaParametro({
  parametro: p,
  bozza,
  onCambia,
  onRipristina,
}: {
  parametro: ParametroRicetta;
  bozza: Bozza | undefined;
  onCambia: (v: Bozza) => void;
  onRipristina: () => void;
}) {
  const cambiato = eDiverso(p, bozza);
  const valido = bozzaValida(p, bozza);

  /* Un valore non scalare si mostra e si copia: modificarlo da un campo di
     testo vorrebbe dire fidarsi di un JSON scritto a mano dentro un modulo. */
  if (!modificabile(p)) {
    return (
      <div className="tb-parametro">
        <div className="tb-parametro__testata">
          <span className="tb-parametro__nome">
            {p.etichetta}
            <span className="sb-nota" style={{ display: 'block' }}>
              non è un numero né un interruttore: si copia com’è
            </span>
          </span>
          <span className="tb-voce__valore">{p.testo}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-parametro" role="group" aria-label={p.etichetta}>
      <div className="tb-parametro__testata">
        <span className="tb-parametro__nome">
          {p.etichetta}
          <span className="sb-nota" style={{ display: 'block' }}>
            <code>{p.chiave}</code>
          </span>
        </span>
        {cambiato ? (
          <span className="tb-transizione">
            <span className="tb-transizione__da tb-transizione__da--sostituito">{p.testo}</span>
            <span className="tb-transizione__freccia" aria-hidden="true">
              →
            </span>
            <span className="tb-transizione__a tb-transizione__a--cambiato">
              {testoValore(valoreDaBozza(p, bozza))}
            </span>
          </span>
        ) : (
          <span className="tb-voce__valore">{p.testo}</span>
        )}
      </div>

      {p.tipo === 'booleano' ? (
        <Interruttore
          etichetta={bozza === true ? 'Acceso' : 'Spento'}
          acceso={bozza === true}
          onCambia={() => onCambia(!(bozza === true))}
        />
      ) : (
        <Campo
          etichetta="Valore"
          type={p.tipo === 'numero' ? 'number' : 'text'}
          inputMode={p.tipo === 'numero' ? 'decimal' : undefined}
          step="any"
          testuale={p.tipo === 'testo'}
          value={typeof bozza === 'string' ? bozza : ''}
          onChange={(e) => onCambia(e.target.value)}
          errore={!valido ? 'Serve un numero.' : null}
        />
      )}

      {cambiato && (
        <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
          <Distintivo tono="cautela">diverso dal backtest</Distintivo>
          <Pulsante variante="nudo" onClick={onRipristina}>
            Rimetti {p.testo}
          </Pulsante>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * Il riepilogo degli scostamenti
 * ======================================================================== */

function RiepilogoScostamenti({
  scostamenti,
  idBacktest,
}: {
  scostamenti: Scostamento[];
  idBacktest: number | null;
}) {
  const veri = scostamenti.filter((s) => !s.soloNominale);
  const nominali = scostamenti.filter((s) => s.soloNominale);

  return (
    <Scheda tono={veri.length > 0 ? 'cautela' : undefined}>
      <Occhiello>Differenze dal backtest #{idBacktest}</Occhiello>

      {veri.length > 0 && (
        <Avvertenza livello="cautela">
          <b>
            Questo bot non corrisponde più al backtest #{idBacktest}: {veri.length}{' '}
            {veri.length === 1 ? 'valore è diverso' : 'valori sono diversi'}.
          </b>{' '}
          Il profitto, il drawdown e le operazioni misurati su quel backtest sono stati ottenuti con
          i valori di sinistra. Con quelli di destra non sono mai stati misurati: non valgono per
          questo bot.
        </Avvertenza>
      )}

      <div className="tb-voci" style={{ marginTop: 'var(--sb-s-5)' }}>
        {scostamenti.map((s) => (
          <div className="tb-voce" key={s.chiave}>
            <span className="tb-voce__chiave">
              {s.campo}
              {s.soloNominale && (
                <span className="sb-nota" style={{ display: 'block' }}>
                  stessa cosa con un altro ticker: il bot corrisponde ancora alla misura
                </span>
              )}
            </span>
            <span className="tb-transizione">
              <span className="tb-transizione__da tb-transizione__da--sostituito">{s.da}</span>
              <span className="tb-transizione__freccia" aria-hidden="true">
                →
              </span>
              <span
                className={
                  s.soloNominale ? 'tb-transizione__a' : 'tb-transizione__a tb-transizione__a--cambiato'
                }
              >
                {s.a}
              </span>
            </span>
          </div>
        ))}
      </div>

      {nominali.length > 0 && veri.length === 0 && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
          Le differenze sono solo di ticker — token avvolti e stablecoin equiparate — quindi la
          configurazione è ancora quella misurata.
        </div>
      )}
    </Scheda>
  );
}

/* ==========================================================================
 * Il mercato del segnale
 * ======================================================================== */

function SegnaleMercato({
  mercati,
  stato,
  errore,
  scelto,
  onScegli,
  onRicarica,
  asset,
  currency,
  exchangeIdSuggerito,
  onCreato,
}: {
  mercati: MarketWatcher[];
  stato: 'caricamento' | 'pieno' | 'vuoto' | 'errore';
  errore?: string;
  scelto: number | null;
  onScegli: (id: number) => void;
  onRicarica: () => void;
  asset: string;
  currency: string;
  exchangeIdSuggerito: number | null;
  onCreato: (id: number) => void;
}) {
  const [apertaCreazione, setApertaCreazione] = useState(false);

  const mercato = mercati.find((m) => m.id === scelto) ?? null;

  /* Il mercato del segnale e la coppia eseguita quasi mai coincidono su catena:
     segnale BTC/USDT su Binance, esecuzione BTCB/USDT su BSC. Lo scarto è
     legittimo, ma va detto — il prezzo della pool può discostarsi da quello del
     mercato di riferimento. */
  const coppiaSegnale = mercato ? `${mercato.asset}/${mercato.currency}` : null;
  const coppiaEsecuzione = asset.trim() && currency.trim() ? `${asset.trim()}/${currency.trim()}` : null;
  const discordanza =
    coppiaSegnale != null &&
    coppiaEsecuzione != null &&
    coppiaSegnale.toUpperCase() !== coppiaEsecuzione.toUpperCase();

  const voci: VoceSelettore[] = [
    { valore: '', etichetta: '— scegli il mercato del segnale —' },
    ...mercati.map((m) => ({
      valore: String(m.id),
      etichetta: `${m.asset}/${m.currency} su ${m.exchange?.name ?? m.exchange?.slug ?? `exchange ${m.exchangeId}`}${m.isActive === false ? ' (non attivo)' : ''}`,
    })),
  ];

  return (
    <Scheda>
      <Occhiello>Il segnale — da dove arrivano le candele</Occhiello>
      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        È il mercato che alimenta la strategia, non quello su cui si opera. Il backend lo richiede:
        un bot senza mercato del segnale non si può creare.
      </div>

      {stato === 'errore' ? (
        <Avvertenza livello="grave">
          L’elenco dei mercati non è disponibile{errore ? `: ${errore}` : '.'}{' '}
          <button
            type="button"
            onClick={onRicarica}
            style={{
              all: 'unset',
              cursor: 'pointer',
              color: 'var(--sb-accento-chiaro)',
              textDecoration: 'underline',
              minHeight: 'var(--sb-tocco-min)',
            }}
          >
            Riprova
          </button>
        </Avvertenza>
      ) : (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Selettore
            etichetta="Mercato del segnale"
            suggerimento={
              stato === 'caricamento'
                ? 'lettura…'
                : `${mercati.length} ${mercati.length === 1 ? 'mercato' : 'mercati'}`
            }
            valore={scelto == null ? '' : String(scelto)}
            voci={voci}
            onCambia={(v) => v && onScegli(Number(v))}
          />
        </div>
      )}

      {stato === 'vuoto' && (
        <Avvertenza livello="cautela">
          Non c’è nessun mercato del segnale registrato. Senza, il bot non si può creare: creane uno
          qui sotto — serve solo a scaricare le candele pubbliche, nessuna credenziale.
        </Avvertenza>
      )}

      <div style={{ marginTop: 'var(--sb-s-5)' }}>
        <Pulsante variante="tenue" onClick={() => setApertaCreazione(true)}>
          Crea un mercato del segnale
        </Pulsante>
      </div>

      {discordanza && (
        <Avvertenza livello="nota">
          Il segnale arriva da <b>{coppiaSegnale}</b>, l’esecuzione avviene su{' '}
          <b>{coppiaEsecuzione}</b>. È normale su catena — il ticker on-chain è un altro — ma il
          prezzo della pool può discostarsi da quello del mercato di riferimento.
        </Avvertenza>
      )}

      <NuovoMercato
        aperta={apertaCreazione}
        onChiudi={() => setApertaCreazione(false)}
        asset={asset}
        currency={currency}
        exchangeIdSuggerito={exchangeIdSuggerito}
        onCreato={(id) => {
          setApertaCreazione(false);
          onCreato(id);
        }}
      />
    </Scheda>
  );
}

function NuovoMercato({
  aperta,
  onChiudi,
  asset,
  currency,
  exchangeIdSuggerito,
  onCreato,
}: {
  aperta: boolean;
  onChiudi: () => void;
  asset: string;
  currency: string;
  exchangeIdSuggerito: number | null;
  onCreato: (id: number) => void;
}) {
  const [a, setA] = useState('');
  const [c, setC] = useState('');
  const [idExchange, setIdExchange] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const richiesta = usaRichiesta(() => exchanges.elenco({ sort: ['name,ASC'] }), [], {
    attiva: aperta,
  });
  const elenco = richiesta.dati?.data ?? [];

  useEffect(() => {
    if (!aperta) return;
    setA(asset.trim().toUpperCase());
    setC(currency.trim().toUpperCase());
    setIdExchange(exchangeIdSuggerito != null ? String(exchangeIdSuggerito) : '');
    setErrore(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperta]);

  const scelto = elenco.find((e) => String(e.id) === idExchange) ?? null;
  const motivo = !idExchange
    ? 'Scegli l’exchange da cui scaricare le candele.'
    : !a.trim() || !c.trim()
      ? 'Indica asset e valuta del segnale.'
      : scelto && !scelto.isCCXTSupported
        ? `${scelto.slug} non è più supportato da ccxt: le candele non arriverebbero.`
        : null;

  async function salva() {
    if (motivo || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      const nato = await marketWatchers.crea({
        exchangeId: Number(idExchange),
        asset: a.trim().toUpperCase(),
        currency: c.trim().toUpperCase(),
        isActive: true,
      });
      onCreato(nato.id);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Creazione non riuscita.');
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={aperta}
      onChiudi={onChiudi}
      titolo="Nuovo mercato del segnale"
      sottotitolo="solo candele pubbliche: nessuna credenziale"
      pieda={
        <>
          <Pulsante onClick={onChiudi} disabled={inCorso}>
            Annulla
          </Pulsante>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Pulsante variante="primario" largo onClick={salva} disabled={!!motivo || inCorso}>
              {inCorso ? 'Creazione…' : 'Crea'}
            </Pulsante>
            {motivo && <span className="sb-motivo-blocco">{motivo}</span>}
          </div>
        </>
      }
    >
      <Selettore
        etichetta="Exchange"
        suggerimento={richiesta.stato === 'caricamento' ? 'lettura…' : `${elenco.length} registrati`}
        valore={idExchange}
        voci={[
          { valore: '', etichetta: '— scegli —' },
          ...elenco.map((e) => ({
            valore: String(e.id),
            etichetta: e.name,
            motivoSpento: e.isCCXTSupported ? undefined : 'non più supportato da ccxt',
          })),
        ]}
        onCambia={setIdExchange}
      />
      <Campo etichetta="Asset del segnale" testuale value={a} onChange={(e) => setA(e.target.value)} />
      <Campo
        etichetta="Valuta del segnale"
        testuale
        value={c}
        onChange={(e) => setC(e.target.value)}
      />
      {errore && <Avvertenza livello="grave">{errore}</Avvertenza>}
    </Modale>
  );
}

/* ==========================================================================
 * La sezione di catena
 * ======================================================================== */

type SuggerimentoGas = NonNullable<Catena['gasDefaults']>;

function SezioneCatena({
  catene,
  stato,
  errore,
  onRicarica,
  catenaScelta,
  onCatena,
  catena,
  dexScelto,
  onDex,
  rpc,
  onRpc,
  esitoRpc,
  inProva,
  onProva,
  gasLimit,
  onGasLimit,
  minGas,
  onMinGas,
  ricaricaGas,
  onRicaricaGas,
  slippage,
  onSlippage,
  suggerimento,
}: {
  catene: Catena[];
  stato: 'caricamento' | 'pieno' | 'vuoto' | 'errore';
  errore?: string;
  onRicarica: () => void;
  catenaScelta: number | null;
  onCatena: (id: number | null) => void;
  catena: Catena | null;
  dexScelto: Dex;
  onDex: (d: Dex) => void;
  rpc: string;
  onRpc: (v: string) => void;
  esitoRpc: EsitoProvaRpc | null;
  inProva: boolean;
  onProva: () => void;
  gasLimit: string;
  onGasLimit: (v: string) => void;
  minGas: string;
  onMinGas: (v: string) => void;
  ricaricaGas: string;
  onRicaricaGas: (v: string) => void;
  slippage: string;
  onSlippage: (v: string) => void;
  suggerimento: SuggerimentoGas | null;
}) {
  const nativo = suggerimento?.nativeSymbol ?? catena?.nativeSymbol ?? '';
  const predefinito = SLIPPAGE_PREDEFINITO[dexScelto];

  if (stato === 'errore') {
    return (
      <Avvertenza livello="grave">
        L’elenco delle catene non è disponibile{errore ? `: ${errore}` : '.'} Senza, non si può
        configurare un bot su catena.{' '}
        <button
          type="button"
          onClick={onRicarica}
          style={{
            all: 'unset',
            cursor: 'pointer',
            color: 'var(--sb-accento-chiaro)',
            textDecoration: 'underline',
            minHeight: 'var(--sb-tocco-min)',
          }}
        >
          Riprova
        </button>
      </Avvertenza>
    );
  }

  return (
    <div style={{ marginTop: 'var(--sb-s-6)' }}>
      <div className="tb-nuovo__griglia">
        <Selettore
          etichetta="Catena"
          suggerimento={stato === 'caricamento' ? 'lettura…' : `${catene.length} supportate`}
          valore={catenaScelta == null ? '' : String(catenaScelta)}
          voci={[
            { valore: '', etichetta: '— scegli —' },
            ...catene.map((c) => ({ valore: String(c.chainId), etichetta: `${c.name} · ${c.nativeSymbol}` })),
          ]}
          onCambia={(v) => onCatena(v === '' ? null : Number(v))}
        />
        <Selettore
          etichetta="DEX"
          suggerimento="chi esegue lo swap"
          valore={dexScelto}
          voci={DEX_DISPONIBILI.map((d) => ({ valore: d, etichetta: ETICHETTE_DEX[d] }))}
          onCambia={(v) => onDex(v as Dex)}
        />
      </div>

      {catena && (
        <>
          <div style={{ marginTop: 'var(--sb-s-5)' }}>
            {catena.rpcs.length > 0 && (
              <Selettore
                etichetta="RPC noti per questa catena"
                suggerimento={`${catena.rpcs.length} in elenco`}
                valore={catena.rpcs.some((r) => r.url === rpc) ? rpc : ''}
                voci={[
                  { valore: '', etichetta: '— oppure scrivilo sotto —' },
                  ...catena.rpcs.map((r) => ({ valore: r.url, etichetta: r.label })),
                ]}
                onCambia={onRpc}
              />
            )}
          </div>

          <div style={{ marginTop: 'var(--sb-s-5)' }}>
            <Campo
              etichetta="RPC"
              suggerimento="endpoint JSON-RPC"
              testuale
              autoComplete="off"
              value={rpc}
              onChange={(e) => onRpc(e.target.value)}
              errore={
                esitoRpc && !esitoRpc.chainIdMatches && esitoRpc.chainId != null
                  ? `Risponde per la catena ${esitoRpc.chainId}, non per la ${catenaScelta}.`
                  : null
              }
            />
            <div className="sb-riga" style={{ marginTop: 'var(--sb-s-4)', gap: 'var(--sb-s-4)' }}>
              <Pulsante variante="tenue" onClick={onProva} disabled={!rpc.trim() || inProva}>
                {inProva ? 'Prova in corso…' : 'Prova l’RPC'}
              </Pulsante>
              {esitoRpc && (
                <span className={esitoRpc.ok ? 'sb-nota' : 'tb-motivo tb-motivo--grave'}>
                  {esitoRpc.message}
                </span>
              )}
            </div>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
              Un endpoint parla di una catena sola: se risponde per un’altra, il bot firmerebbe le
              transazioni dove non deve. La prova legge anche il gas price reale e ritara i valori
              qui sotto.
            </div>
          </div>

          <div className="tb-nuovo__griglia" style={{ marginTop: 'var(--sb-s-6)' }}>
            <CampoGas
              etichetta="Limite di gas per transazione"
              suggerimento={suggerimento ? `consigliato ${num(suggerimento.gasLimit)}` : undefined}
              valore={gasLimit}
              onCambia={onGasLimit}
              consigliato={suggerimento ? String(suggerimento.gasLimit) : null}
              nota={
                suggerimento
                  ? `Uno swap aggregato consuma circa ${num(suggerimento.swapGasUnits)} gas. Sotto quella soglia la transazione esaurisce il gas a metà: viene annullata dalla rete e il gas consumato si paga comunque.`
                  : 'Il consigliato non è disponibile: arriva dal profilo della catena.'
              }
              errore={
                gasLimit !== '' && suggerimento && Number(gasLimit) < suggerimento.swapGasUnits
                  ? `Sotto i ${num(suggerimento.swapGasUnits)} gas di uno swap: ogni tentativo fallirebbe pagando il gas.`
                  : null
              }
            />
            <CampoGas
              etichetta={`Riserva minima di gas${nativo ? ` (${nativo})` : ''}`}
              suggerimento={
                suggerimento
                  ? `consigliato ${suggerimento.minimumBaseCoinBalance} · ${suggerimento.swapsForMinimum} swap`
                  : undefined
              }
              valore={minGas}
              onCambia={onMinGas}
              consigliato={suggerimento ? String(suggerimento.minimumBaseCoinBalance) : null}
              nota="Sotto questa soglia il bot vende parte della posizione per ricomprare la moneta nativa."
              errore={
                minGas !== '' && suggerimento && Number(minGas) < suggerimento.costPerSwap
                  ? `Meno del costo di un solo swap (${suggerimento.costPerSwap} ${nativo}): il bot può restare senza gas con la posizione aperta, e senza gas non può nemmeno chiuderla.`
                  : null
              }
            />
            <CampoGas
              etichetta={`Ricarica di gas${nativo ? ` (${nativo})` : ''}`}
              suggerimento={
                suggerimento
                  ? `consigliato ${suggerimento.rechargeQtyBaseCoinBalance} · ${suggerimento.swapsForRecharge} swap`
                  : undefined
              }
              valore={ricaricaGas}
              onCambia={onRicaricaGas}
              consigliato={suggerimento ? String(suggerimento.rechargeQtyBaseCoinBalance) : null}
              nota="Quanta moneta nativa il bot ricompra quando scatta la soglia."
              errore={
                ricaricaGas !== '' && minGas !== '' && Number(ricaricaGas) <= Number(minGas)
                  ? 'La ricarica non porta il saldo sopra la soglia minima: appena finita, la soglia scatta di nuovo.'
                  : null
              }
            />
            <Campo
              etichetta="Slippage massimo sullo swap"
              suggerimento={
                predefinito == null
                  ? `vuoto = predefinito del backend (0,5%)`
                  : `vuoto = ${predefinito}% su ${ETICHETTE_DEX[dexScelto]}`
              }
              type="number"
              step="any"
              min={0}
              inputMode="decimal"
              unita="%"
              value={slippage}
              onChange={(e) => onSlippage(e.target.value)}
            />
          </div>

          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            Se lo slippage atteso supera la soglia, il bot non esegue lo swap. Su{' '}
            {ETICHETTE_DEX.VELORA} il costo reale misurato è di pochi punti base: una tolleranza più
            larga non protegge, autorizza un sandwich.
          </div>
        </>
      )}
    </div>
  );
}

function CampoGas({
  etichetta,
  suggerimento,
  valore,
  onCambia,
  consigliato,
  nota,
  errore,
}: {
  etichetta: string;
  suggerimento?: string;
  valore: string;
  onCambia: (v: string) => void;
  consigliato: string | null;
  nota: string;
  errore: string | null;
}) {
  return (
    <div>
      <Campo
        etichetta={etichetta}
        suggerimento={suggerimento}
        type="number"
        step="any"
        min={0}
        inputMode="decimal"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
        errore={errore}
      />
      {consigliato != null && valore !== consigliato && (
        <div style={{ marginTop: 'var(--sb-s-3)' }}>
          <Pulsante variante="nudo" onClick={() => onCambia(consigliato)}>
            Usa {consigliato}
          </Pulsante>
        </div>
      )}
      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        {nota}
      </div>
    </div>
  );
}

/* ==========================================================================
 * Denaro vero — da solo, in fondo
 * ======================================================================== */

function SezioneDenaro({
  denaroVero,
  capitale,
  valuta,
  nomeSede,
  onSpegni,
  onChiediAccensione,
}: {
  denaroVero: boolean;
  capitale: { valido: boolean; valore: number; motivo: string };
  valuta: string;
  nomeSede: string | null;
  onSpegni: () => void;
  onChiediAccensione: () => void;
}) {
  return (
    <Scheda tono={denaroVero ? 'cautela' : undefined}>
      <div className="tb-parametro__testata">
        <Occhiello>Capitale reale o simulato</Occhiello>
        <Distintivo tono={denaroVero ? 'cautela' : 'neutro'}>
          {denaroVero ? 'REALE' : 'SIMULATO'}
        </Distintivo>
      </div>

      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        È la scelta più pericolosa di questa schermata, e sta da sola per questo. Il valore di
        partenza è <b>simulato</b>, sempre: in simulazione il bot fa gli stessi calcoli e scrive gli
        stessi numeri, senza mandare ordini.
      </div>

      <div style={{ marginTop: 'var(--sb-s-6)' }}>
        {denaroVero ? (
          <>
            <Avvertenza livello="cautela">
              Il bot comprerà e venderà <b>davvero</b>, impegnando{' '}
              <b>
                {capitale.valido ? num(capitale.valore, 2) : '—'} {valuta}
              </b>
              {nomeSede ? ` su «${nomeSede}»` : ''}.
            </Avvertenza>
            <Pulsante onClick={onSpegni}>Torna in simulazione</Pulsante>
          </>
        ) : (
          <>
            <Pulsante variante="cautela" onClick={onChiediAccensione} disabled={!capitale.valido}>
              Passa a denaro vero
            </Pulsante>
            {!capitale.valido && (
              <span className="sb-motivo-blocco" style={{ display: 'block', marginTop: 'var(--sb-s-3)' }}>
                Prima il capitale: la conferma deve poter ripetere l’importo che stai per impegnare.
              </span>
            )}
          </>
        )}
      </div>
    </Scheda>
  );
}

/* ==========================================================================
 * Il riepilogo della conferma
 * ======================================================================== */

function Riepilogo({
  strategia,
  asset,
  currency,
  candela,
  warmup,
  quantiParametri,
  scostamenti,
  idBacktest,
  bersaglio,
  mercato,
  capitale,
  valuta,
  saldoParziale,
  denaroVero,
  avviaSubito,
  conTelegram,
  dex,
  nomeCatena,
}: {
  strategia: string;
  asset: string;
  currency: string;
  candela: number;
  warmup: number;
  quantiParametri: number;
  scostamenti: Scostamento[];
  idBacktest: number | null;
  bersaglio: Bersaglio | null;
  mercato: MarketWatcher | null;
  capitale: number;
  valuta: string;
  saldoParziale: boolean;
  denaroVero: boolean;
  avviaSubito: boolean;
  conTelegram: boolean;
  dex: ConfigurazioneDex | null;
  nomeCatena: string | null;
}) {
  const veri = scostamenti.filter((s) => !s.soloNominale);

  const voci: [string, string][] = [
    ['Strategia', strategia],
    ['Coppia', `${asset.toUpperCase()}/${currency.toUpperCase()}`],
    ['Candela', `${candela} min`],
    ['Warm-up', `${num(warmup)} candele`],
    ['Parametri copiati', quantiParametri === 1 ? '1' : String(quantiParametri)],
    ['Segnale', mercato ? `${mercato.asset}/${mercato.currency} su ${mercato.exchange?.name ?? `exchange ${mercato.exchangeId}`}` : '—'],
    ['Sede', bersaglio?.nome ?? '—'],
    ['Capitale', `${num(capitale, 2)} ${valuta}`],
    ['Tetto al capitale', saldoParziale ? 'sì, solo l’importo indicato' : 'no, tutto il saldo del conto'],
    ['Notifiche Telegram', conTelegram ? 'attive' : 'nessuna'],
    ['Alla creazione', avviaSubito ? 'parte subito' : 'resta fermo'],
  ];

  if (dex && nomeCatena) {
    voci.splice(7, 0, ['Catena', `${nomeCatena} · ${ETICHETTE_DEX[dex.dex]}`]);
  }

  return (
    <>
      <Avvertenza livello={denaroVero ? 'cautela' : 'nota'}>
        {denaroVero ? (
          <>
            Il bot nasce con <b>denaro vero</b> e impegna{' '}
            <b>
              {num(capitale, 2)} {valuta}
            </b>
            {bersaglio ? ` su «${bersaglio.nome}»` : ''}.{' '}
            {avviaSubito
              ? 'Parte appena creato e può comprare alla prima candela utile.'
              : 'Resta fermo finché non lo avvii tu.'}
          </>
        ) : (
          <>
            Il bot nasce <b>simulato</b>: fa i suoi calcoli e scrive i suoi numeri, senza mandare
            ordini. Nessun capitale viene impegnato.
          </>
        )}
      </Avvertenza>

      {idBacktest != null && veri.length > 0 && (
        <Avvertenza livello="cautela">
          <b>
            Non corrisponde al backtest #{idBacktest}: {veri.length}{' '}
            {veri.length === 1 ? 'valore diverso' : 'valori diversi'}
          </b>{' '}
          — {veri.map((s) => `${s.campo} ${s.da} → ${s.a}`).join('; ')}. I risultati di quel
          backtest non valgono per questo bot.
        </Avvertenza>
      )}

      {idBacktest != null && veri.length === 0 && (
        <Avvertenza livello="nota">
          La configurazione è identica al backtest #{idBacktest}: strategia, parametri, coppia e
          candela coincidono.
        </Avvertenza>
      )}

      <div className="tb-voci">
        {voci.map(([k, v]) => (
          <div className="tb-voce" key={k}>
            <span className="tb-voce__chiave">{k}</span>
            <span className="tb-voce__valore">{v}</span>
          </div>
        ))}
      </div>
    </>
  );
}
